import { Alert, InputAdornment } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AddressElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import config from '../../../config';
import { sendInsuranceForm } from '../../../ducks/form.duck';
import { getClientData, setClientData } from '../../../util/clientData';
import useAppTheme from '../../../util/hooks/useAppTheme';
import { stripeElementsAppearance } from '../../../util/stripe';
import PickersLocalizationProvider from '../../BasicUI/PickersLocalizationProvider/PickersLocalizationProvider';
import IconBank from '../../Icons/IconBank';
import AnimatedLogo from '../../Logo/VanSite/AnimatedLogo';

const stripe = loadStripe(config.stripe.publishableKey);

const formatIban = (value) => {
  const alphanum = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return alphanum.match(/.{1,4}/g)?.join(' ') || '';
};

const isValidIban = (value) => {
  const raw = value.replace(/\s/g, '');
  return /^[A-Z]{2}[0-9]{2,30}$/.test(raw);
};

const FormComponent = ({ sendData, setSendData }) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const containerRef = useRef(null);

  const [addressComplete, setAddressComplete] = useState(false);
  const [iban, setIban] = useState('');
  const [touched, setTouched] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [email, setEmail] = useState('');
  const [shareData, setShareData] = useState(false);
  const [avb, setAvb] = useState(false);
  const [annualPassCode, setAnnualPassCode] = useState('');

  const [elementRendering, setElementRendering] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    if (!containerRef.current) return;

    try {
      return await executeRecaptcha('newsletter');
    } catch (e) {
      // noop
    }
  }, [executeRecaptcha]);


  const handleSubmit = async (event) => {
    try {
      event.preventDefault();

      const token = await handleReCaptchaVerify();
      if (!token) return;

      if (!stripe || !elements) {
        return;
      }
      setLoading(true);
      const addressElement = elements.getElement('address');
      const { value: addressValue } = await addressElement.getValue();
      await dispatch(sendInsuranceForm({
        iban: iban.replace(/ /g, ''),
        dateOfBirth: dateOfBirth.format('DD.MM.YYYY'),
        email,
        shareData,
        annualPassCode,
        ...addressValue,
      }));
      setClientData('vansite.sendInsuranceForm', true, moment().add(99, 'years'));
      setSendData(true);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!elements) return;

    const addressElement = elements.getElement('address');

    if (addressElement) {
      addressElement.on('change', (event) => {
        setAddressComplete(event.complete);
      });
      addressElement.on('ready', () => setElementRendering(false));
    }
  }, [elements]);

  const hasError = touched && !isValidIban(iban);

  return (
    <Box>
      {!sendData ? (
        <Box component="form" onSubmit={handleSubmit} sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {elementRendering ? <AnimatedLogo /> : null}
          <Stack spacing={2}
                 sx={{ visibility: elementRendering ? 'hidden' : 'visible', height: elementRendering ? 0 : '100%' }}>
            <Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <AddressElement
                  options={{
                    mode: 'billing',
                    fields: {
                      phone: 'always',
                    },
                    validation: {
                      phone: {
                        required: 'always',
                      },
                    },
                    display: {
                      name: 'split',
                    },
                  }} />
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <InputLabel style={{
                  fontSize: '1rem',
                }}>
                  {intl.formatMessage({ id: 'StoryblokFormPreset.dateOfBirth' })}
                </InputLabel>
                <PickersLocalizationProvider>
                  <DatePicker
                    onChange={(newValue) => setDateOfBirth(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        InputProps: {
                          required: true,
                        },
                      },
                    }}
                    sx={(theme) => ({
                      width: '100%',
                      ...(!dateOfBirth ? {
                        '& .MuiOutlinedInput-root > .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.warning.main,
                        },
                      } : {}),
                      '& .Mui-focused > .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '1px !important',
                      },
                    })}
                    value={dateOfBirth}
                  />
                </PickersLocalizationProvider>
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <InputLabel style={{
                  fontSize: '0.93rem',
                }}>
                  {intl.formatMessage({ id: 'StoryblokFormPreset.email' })}
                </InputLabel>
                <TextField
                  fullWidth
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  size="small"
                  sx={(theme) => ({
                    width: '100%',
                    ...(!email ? {
                      '& .MuiOutlinedInput-root > .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.warning.main,
                      },
                    } : {}),
                    '& .Mui-focused > .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '1px !important',
                    },
                  })}
                  type="email"
                  value={email}
                />
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <InputLabel style={{
                  fontSize: '0.93rem',
                }}>
                  {intl.formatMessage({ id: 'StoryblokFormPreset.iban' })}
                </InputLabel>
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconBank />
                      </InputAdornment>
                    ),
                  }}
                  error={hasError}
                  fullWidth
                  helperText={hasError ? intl.formatMessage({ id: 'StoryblokFormPreset.ibanError' }) : ''}
                  onBlur={() => setTouched(true)}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/\s/g, '').toUpperCase();
                    const formatted = formatIban(rawValue);
                    setIban(formatted);
                  }}
                  placeholder="DE00 0000 0000 0000 0000 00"
                  required
                  size="small"
                  sx={(theme) => ({
                    width: '100%',
                    ...(!iban ? {
                      '& .MuiOutlinedInput-root > .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.warning.main,
                      },
                    } : {}),
                    '& .Mui-focused > .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '1px !important',
                    },
                  })}
                  type="text"
                  value={iban}
                />
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <InputLabel style={{
                  fontSize: '0.93rem',
                }}>
                  {intl.formatMessage({ id: 'StoryblokFormPreset.annualPassCode' })}
                </InputLabel>
                <TextField
                  fullWidth
                  onChange={(e) => setAnnualPassCode(e.target.value)}
                  size="small"
                  sx={(theme) => ({
                    width: '100%',
                  })}
                  type="text"
                  value={annualPassCode}
                />
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={shareData}
                                                       onChange={(e) => setShareData(e.target.checked)} />}
                                    label={intl.formatMessage({ id: 'StoryblokFormPreset.shareData' })}
                                    required />
                </FormGroup>
              </Box>
              <Box sx={{ width: '100%', marginBottom: '0.75rem', marginTop: '-0.3rem' }}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={avb}
                                                       onChange={(e) => setAvb(e.target.checked)} />}
                                    label={intl.formatMessage({ id: 'StoryblokFormPreset.avb' }, {
                                      url: <a
                                        href="https://portal-dsg.s3.eu-central-1.amazonaws.com/cms/global/DSG_Camping-Schutz_AGB_AVB.pdf"
                                        rel="noreferrer" target="_blank">
                                        {intl.formatMessage({ id: 'StoryblokFormPreset.avb.url' })}
                                      </a>,
                                    })}
                                    required />
                </FormGroup>
              </Box>
            </Box>
            <Box width="100%">
              <Box id="insuranceFormContainer" ref={containerRef} />
            </Box>
            <Button
              color="success"
              disabled={!(iban && addressComplete && dateOfBirth && shareData && avb)}
              fullWidth
              loading={loading}
              size="large"
              type="submit"
              variant="contained"
            >
              {intl.formatMessage({ id: 'StoryblokFormPreset.submit' })}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Alert type="success"
        >
          <FormattedMessage id="StoryblokFormPreset.success" />
        </Alert>
      )}
    </Box>

  );
};

/**
 * StoryblokPassPreset component displays information about an annual pass with benefits,
 * pricing, and a call-to-action button alongside an image.
 *
 * @param {object} props - Component props
 * @param {object} props.blok - Storyblok block data
 * @returns {React.ReactElement} StoryblokPassPreset component
 */
const StoryblokFormPreset = ({ blok }) => {
  const intl = useIntl();
  const theme = useAppTheme();

  const hasSendBefore = getClientData('vansite.sendInsuranceForm') || false;
  const [sendData, setSendData] = useState(hasSendBefore);

  /** @type {import('@stripe/stripe-js').StripeElementsOptions} */
  const stripeElementsOptions = useMemo(
    () => ({
      locale: intl.locale,
      mode: 'setup',
      currency: config.currency.toLowerCase(),
      paymentMethodTypes: ['sepa_debit'],
      appearance: stripeElementsAppearance(theme),
    }),
    [intl.locale, theme],
  );

  if (sendData) {
    return (
      <Box>
        <Alert type="success">
          <FormattedMessage id="StoryblokFormPreset.success" />
        </Alert>
      </Box>
    );
  }

  return (
    <GoogleReCaptchaProvider
      container={{
        element: 'insuranceFormContainer',
        parameters: {
          badge: 'inline',
        },
      }}
      language={intl.locale}
      reCaptchaKey={config.google.recaptcha}
    >
      <Elements options={stripeElementsOptions} stripe={stripe}>
        <FormComponent sendData={sendData} setSendData={setSendData} />
      </Elements>
    </GoogleReCaptchaProvider>
  );
};

export default StoryblokFormPreset;
